import React, { useCallback, useMemo, useState } from "react";
import { Add as AddIcon, Shield as ShieldIcon } from "@mui/icons-material";
import { useAlert } from "../../context/AlertProvider";
import { useHasPermission } from "../../hooks/useHasPermission";
import useDebouncedSearch from "../../hooks/useDebouncedSearch";
import usePersistedFilters from "../../hooks/usePersistedFilters";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { ListPageShell, ListToolbar } from "../../components/organisms/list";
import RoleTemplatesList from "./RoleTemplatesList";
import RoleTemplateFormDrawer from "./RoleTemplateFormDrawer";
import { loadStoredTemplates, saveStoredTemplates } from "./constants";

const DEFAULT_FILTERS = { query: "" };

/**
 * Admin personel (AdminStaff) rol şablonları. Server API'si yok — kayıtlar
 * localStorage'da tutulur (bkz. constants.ROLE_TPL_STORAGE_KEY), bu yüzden
 * sayfalama/filtreleme istemci taraflıdır. Liste kabuğu diğer yönetim
 * sayfalarıyla aynı: ListPageShell + ListToolbar + ListView.
 */
export default function RoleTemplates() {
  const { showSuccess } = useAlert();
  const canCreate = useHasPermission("admin-roles:create");
  const canUpdate = useHasPermission("admin-roles:update");
  const canDelete = useHasPermission("admin-roles:delete");

  const [templates, setTemplates] = useState(() => loadStoredTemplates());
  const persistTemplates = useCallback((updater) => {
    setTemplates((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveStoredTemplates(next);
      return next;
    });
  }, []);

  const [filters, setFilters] = usePersistedFilters("roleTemplates:filters", DEFAULT_FILTERS);
  const { debounced: debouncedQuery, pending: queryPending } = useDebouncedSearch(filters.query);
  const [page, setPage] = useState(1); // 1-tabanlı
  const [pageSize, setPageSize] = useState(10); // ListView gövdesinden ölçülür

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formName, setFormName] = useState("");
  const [formPermissions, setFormPermissions] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, template: null });

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, [setFilters]);

  const clearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
    setPage(1);
  }, [setFilters]);

  const filteredTemplates = useMemo(() => {
    const q = debouncedQuery.trim().toLocaleLowerCase("tr-TR");
    if (!q) return templates;
    return templates.filter((t) => (t.name || "").toLocaleLowerCase("tr-TR").includes(q));
  }, [templates, debouncedQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / pageSize));
  // Render zamanı clamp — bkz. useUserManagement.js safePage notu.
  const safePage = Math.max(1, Math.min(page, totalPages));
  const pagedTemplates = useMemo(
    () => filteredTemplates.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredTemplates, safePage, pageSize]
  );

  const openCreate = useCallback(() => {
    setDrawerMode("create");
    setEditingTemplate(null);
    setFormName("");
    setFormPermissions([]);
    setDrawerOpen(true);
  }, []);

  const openEdit = useCallback((t) => {
    setDrawerMode("edit");
    setEditingTemplate(t);
    setFormName(t.name);
    setFormPermissions([...(t.permissions || [])]);
    setDrawerOpen(true);
  }, []);

  const togglePerm = useCallback((p) => {
    setFormPermissions((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  }, []);

  const toggleGroup = useCallback((items) => {
    setFormPermissions((prev) =>
      items.every((p) => prev.includes(p))
        ? prev.filter((p) => !items.includes(p))
        : [...new Set([...prev, ...items])]
    );
  }, []);

  const handleSave = useCallback(() => {
    if (!formName.trim()) return;
    if (drawerMode === "create") {
      const newTemplate = {
        _id: Date.now().toString(),
        name: formName.trim(),
        permissions: formPermissions,
        isDefault: false,
        code: Math.random().toString(36).slice(2, 8).toUpperCase(),
      };
      persistTemplates((prev) => [...prev, newTemplate]);
      showSuccess("Personel şablonu oluşturuldu");
    } else {
      persistTemplates((prev) =>
        prev.map((t) =>
          t._id === editingTemplate._id
            ? { ...t, name: formName.trim(), permissions: formPermissions }
            : t
        )
      );
      showSuccess("Personel şablonu güncellendi");
    }
    setDrawerOpen(false);
  }, [drawerMode, formName, formPermissions, editingTemplate, persistTemplates, showSuccess]);

  const handleDelete = useCallback(() => {
    const t = deleteDialog.template;
    if (!t) return;
    persistTemplates((prev) => prev.filter((x) => x._id !== t._id));
    showSuccess("Personel şablonu silindi");
    setDeleteDialog({ open: false, template: null });
  }, [deleteDialog.template, persistTemplates, showSuccess]);

  const copyCode = useCallback((code) => {
    navigator.clipboard?.writeText(code);
    showSuccess("Kod kopyalandı");
  }, [showSuccess]);

  const actionItems = canCreate
    ? [{
        label: "Yeni Şablon",
        icon: <AddIcon sx={{ fontSize: 18 }} />,
        onClick: openCreate,
        variant: "primary",
      }]
    : [];

  return (
    <ListPageShell>
      <ListToolbar
        icon={<ShieldIcon sx={{ fontSize: 18 }} />}
        title="Personel Şablonları"
        subtitle="Admin personelinin izin şablonlarını yönetin"
        count={filteredTemplates.length}
        countLabel="şablon"
        actionItems={actionItems}
        searchFields={[
          {
            key: "query",
            placeholder: "Şablon ara",
            value: filters.query,
            onChange: (v) => setFilter("query", v),
            isPending: queryPending,
          },
        ]}
        onClear={clearFilters}
      />

      <RoleTemplatesList
        templates={pagedTemplates}
        page={safePage}
        pageSize={pageSize}
        totalItems={filteredTemplates.length}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onEdit={openEdit}
        onDelete={(t) => setDeleteDialog({ open: true, template: t })}
        onCopyCode={copyCode}
        onCreate={openCreate}
        canCreate={canCreate}
        canUpdate={canUpdate}
        canDelete={canDelete}
        hasFilter={Boolean(debouncedQuery.trim())}
        isLoading={queryPending}
      />

      <RoleTemplateFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        isDefault={Boolean(editingTemplate?.isDefault)}
        name={formName}
        onNameChange={setFormName}
        permissions={formPermissions}
        onTogglePermission={togglePerm}
        onToggleGroup={toggleGroup}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, template: null })}
        onConfirm={handleDelete}
        title="Şablonu Sil"
        message={`"${deleteDialog.template?.name}" şablonunu silmek istediğinize emin misiniz?`}
        confirmText="Sil"
        confirmVariant="error"
      />
    </ListPageShell>
  );
}
